import FilmReelLogo from 'assets/icon/filmReel.png';
import IndependentPressLogo from 'assets/icon/independentpresslogo.png';
import {
  AuthorizeParamsResponse,
  ConsentMetadataResponse,
  establishSession,
  getAuthorizeParams,
  getConsentMetadata
} from 'services/oauthApi';

const OAUTH_FLOW_ACTIVE_KEY = 'oauth_flow_active';
const OAUTH_CLIENT_ID_KEY = 'oauth_client_id';
const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_LOGIN_METHOD_KEY = 'oauth_login_method';
const OAUTH_SESSION_ESTABLISHED_KEY = 'oauth_session_established_state';
/** Survives authorize↔consent bounces; NOT cleared by clearOAuthFlowPreservingConsentGuards. */
const OAUTH_AUTO_RESUME_ATTEMPTED_KEY = 'oauth_auto_resume_attempted_state';
const OAUTH_CONSENT_LOAD_CACHE_KEY = 'oauth_consent_load_cache';

export type OAuthLoginMethod = 'email' | 'phone';

export interface ConsentLoadResult {
  metadata: ConsentMetadataResponse;
  authorizeParams: AuthorizeParamsResponse;
}

/** Dedupes concurrent mounts — one network load per OAuth state (survives remounts in-session). */
const consentLoadByState = new Map<string, Promise<ConsentLoadResult>>();
const consentResultByState = new Map<string, ConsentLoadResult>();

const OAUTH_CLIENT_LOGOS: Record<string, string> = {
  'app-b-client': FilmReelLogo,
  'film-reel': FilmReelLogo,
  'independent-press': IndependentPressLogo
};

export interface OAuthQueryParseResult {
  isOAuthMode: boolean;
  clientId: string | null;
  state: string | null;
  clientName: string | null;
  authorizeParams: AuthorizeParamsResponse | null;
}

export const parseOAuthQuery = (searchParams: URLSearchParams): OAuthQueryParseResult => {
  const clientId = searchParams.get('client_id');
  const state = searchParams.get('state');
  // OAuth flow whenever client_id is present (state needed for APIs later)
  const isOAuthMode = Boolean(clientId);
  if (isOAuthMode && clientId) {
    persistOAuthFlow(clientId, state);
  }
  return {
    isOAuthMode,
    clientId,
    state,
    clientName: null,
    authorizeParams: null
  };
};

export const persistOAuthFlow = (clientId: string, state: string | null): void => {
  try {
    sessionStorage.setItem(OAUTH_FLOW_ACTIVE_KEY, 'true');
    sessionStorage.setItem(OAUTH_CLIENT_ID_KEY, clientId);
    if (state) {
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
    }
  } catch {
    // sessionStorage may be unavailable
  }
};

export const clearOAuthFlow = (): void => {
  try {
    sessionStorage.removeItem(OAUTH_FLOW_ACTIVE_KEY);
    sessionStorage.removeItem(OAUTH_CLIENT_ID_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_METHOD_KEY);
    sessionStorage.removeItem(OAUTH_SESSION_ESTABLISHED_KEY);
    sessionStorage.removeItem(OAUTH_AUTO_RESUME_ATTEMPTED_KEY);
    sessionStorage.removeItem(OAUTH_CONSENT_LOAD_CACHE_KEY);
  } catch {
    // ignore
  }
  consentLoadByState.clear();
  consentResultByState.clear();
};

/**
 * Clear client-facing OAuth markers before leaving for authorize, but KEEP auto-resume
 * guards so a bounce back to /consent cannot restart the network loop.
 */
export const clearOAuthFlowPreservingConsentGuards = (): void => {
  try {
    sessionStorage.removeItem(OAUTH_FLOW_ACTIVE_KEY);
    sessionStorage.removeItem(OAUTH_CLIENT_ID_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_METHOD_KEY);
  } catch {
    // ignore
  }
};

/** Skip duplicate establishSession when oauth-service session already linked for this state. */
export const isOAuthSessionEstablished = (state: string): boolean => {
  try {
    return sessionStorage.getItem(OAUTH_SESSION_ESTABLISHED_KEY) === state;
  } catch {
    return false;
  }
};

export const markOAuthSessionEstablished = (state: string): void => {
  try {
    sessionStorage.setItem(OAUTH_SESSION_ESTABLISHED_KEY, state);
  } catch {
    // ignore
  }
};

export const hasConsentAutoResumeAttempted = (state: string): boolean => {
  try {
    return sessionStorage.getItem(OAUTH_AUTO_RESUME_ATTEMPTED_KEY) === state;
  } catch {
    return false;
  }
};

export const markConsentAutoResumeAttempted = (state: string): void => {
  try {
    sessionStorage.setItem(OAUTH_AUTO_RESUME_ATTEMPTED_KEY, state);
  } catch {
    // ignore
  }
};

const readPersistedConsentLoad = (state: string): ConsentLoadResult | null => {
  try {
    const raw = sessionStorage.getItem(OAUTH_CONSENT_LOAD_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { state?: string } & ConsentLoadResult;
    if (parsed?.state !== state || !parsed.metadata || !parsed.authorizeParams) {
      return null;
    }
    return { metadata: parsed.metadata, authorizeParams: parsed.authorizeParams };
  } catch {
    return null;
  }
};

const writePersistedConsentLoad = (state: string, result: ConsentLoadResult): void => {
  try {
    sessionStorage.setItem(
      OAUTH_CONSENT_LOAD_CACHE_KEY,
      JSON.stringify({
        state,
        metadata: result.metadata,
        authorizeParams: result.authorizeParams
      })
    );
  } catch {
    // ignore
  }
};

/** Sync peek for Consent initial state (avoids spinner flicker on remount). */
export const peekConsentPageData = (state: string | null): ConsentLoadResult | null => {
  if (!state) {
    return null;
  }
  return consentResultByState.get(state) ?? readPersistedConsentLoad(state);
};

/**
 * Single shared load per OAuth state. Concurrent callers await the same Promise;
 * resolved results are cached in memory + sessionStorage (survives full page bounce).
 * Failed loads are evicted so Retry works.
 */
export const loadConsentPageData = (
  state: string,
  loginMethod?: OAuthLoginMethod | null
): Promise<ConsentLoadResult> => {
  const cached = consentResultByState.get(state) ?? readPersistedConsentLoad(state);
  if (cached) {
    consentResultByState.set(state, cached);
    return Promise.resolve(cached);
  }

  const existing = consentLoadByState.get(state);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<ConsentLoadResult> => {
    try {
      if (!isOAuthSessionEstablished(state)) {
        await establishSession(state, loginMethod);
        markOAuthSessionEstablished(state);
      }

      const [metadata, authorizeParams] = await Promise.all([
        getConsentMetadata(state),
        getAuthorizeParams(state)
      ]);

      const result = { metadata, authorizeParams };
      consentResultByState.set(state, result);
      writePersistedConsentLoad(state, result);
      return result;
    } catch (err) {
      consentLoadByState.delete(state);
      throw err;
    }
  })();

  consentLoadByState.set(state, promise);
  return promise;
};

/** Test helper — clear in-flight/cached consent loads without wiping other OAuth keys. */
export const clearConsentLoadCache = (): void => {
  consentLoadByState.clear();
  consentResultByState.clear();
  try {
    sessionStorage.removeItem(OAUTH_CONSENT_LOAD_CACHE_KEY);
  } catch {
    // ignore
  }
};

/** Test helper — simulate full page reload (memory wiped, sessionStorage kept). */
export const clearConsentLoadMemoryOnly = (): void => {
  consentLoadByState.clear();
  consentResultByState.clear();
};

export const persistOAuthLoginMethod = (method: OAuthLoginMethod): void => {
  try {
    sessionStorage.setItem(OAUTH_LOGIN_METHOD_KEY, method);
  } catch {
    // sessionStorage may be unavailable
  }
};

export const getOAuthLoginMethod = (): OAuthLoginMethod | null => {
  try {
    const value = sessionStorage.getItem(OAUTH_LOGIN_METHOD_KEY);
    if (value === 'email' || value === 'phone') {
      return value;
    }
    return null;
  } catch {
    return null;
  }
};

const getHiddenScope = (loginMethod: OAuthLoginMethod): string =>
  loginMethod === 'email' ? 'phone' : 'email';

export const getEffectiveRequestedScopes = (
  requestedScopes: string[],
  loginMethod: OAuthLoginMethod
): string[] => {
  const hiddenScope = getHiddenScope(loginMethod);
  return requestedScopes.filter((scope) => scope !== hiddenScope);
};

export const isConsentSatisfied = (
  approvedScopes: string[],
  requestedScopes: string[],
  loginMethod: OAuthLoginMethod
): boolean => {
  const approved = new Set(getEffectiveRequestedScopes(approvedScopes || [], loginMethod));
  return getEffectiveRequestedScopes(requestedScopes || [], loginMethod).every((scope) =>
    approved.has(scope)
  );
};

export const getStoredOAuthClientId = (): string | null => {
  try {
    return sessionStorage.getItem(OAUTH_CLIENT_ID_KEY);
  } catch {
    return null;
  }
};

export const getStoredOAuthState = (): string | null => {
  try {
    return sessionStorage.getItem(OAUTH_STATE_KEY);
  } catch {
    return null;
  }
};

/** True when the SPA is on an OAuth bridge route (avoid global 401 redirects here). */
export const isOAuthBridgeRoute = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const path = window.location.pathname;
  return path === '/consent' || path === '/auth/consent' || path === '/signin';
};

/** True when URL has client_id or sessionStorage OAuth flag is set (e.g. /consent?state=…). */
export const isOAuthFlowActive = (): boolean => {
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('client_id')) {
        return true;
      }
    }
    return sessionStorage.getItem(OAUTH_FLOW_ACTIVE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const getOAuthClientLogo = (clientId?: string | null): string | null =>
  clientId ? OAUTH_CLIENT_LOGOS[clientId] ?? null : null;

export const loadAuthorizeParams = async (state: string): Promise<AuthorizeParamsResponse> => {
  return getAuthorizeParams(state);
};
